export const TIPS = {
  '/morning': {
    pageLabel: 'MORNING SETUP TIPS',
    intro: 'How to become unstoppable — the operating rhythm for a founder with limited time, unlimited ambition, and a system that holds you accountable.',
    tips: [
      {
        title: 'Start with burning desire',
        body: 'Napoleon Hill studied 500 of the most successful people over 20 years and found the starting point of all achievement isn\'t a plan or discipline — it\'s a burning desire for a definite outcome. Not a wish. Not a preference. An obsession. The equation R = (W × C) + T is the engine. Burning desire is the fuel. On the days when the W feels hard, when you\'d rather refine the deck than send the cold outreach — it\'s the burning desire that pulls you through, not the system. Before you set today\'s W, reconnect with your why.',
      },
      {
        title: 'The night before (2 minutes)',
        body: 'Use the Tomorrow\'s W Seed field in Close the Loop. Plant it before bed. Your subconscious works on it overnight. When you sit down in the morning, the W is already half-formed. This eliminates the most dangerous part of the morning — the 20 minutes of \'what should I work on?\' that turns into email checking and browsing.',
      },
      {
        title: 'Morning ritual (10 minutes, non-negotiable)',
        body: 'Open ResultOS. Go to Morning Setup. The three character cards are staring at you. Before you type anything, ask yourself: \'What am I avoiding?\' — that\'s probably your real W. Type it. Hit submit. The challenge question appears. Answer it honestly. If you flinch, rewrite the W.',
      },
      {
        title: 'Revenue-first W — always',
        body: 'Your W at this stage should almost always be one of two things: talking to potential customers OR shipping something a customer is waiting for. If your W is \'refine the architecture\' or \'research competitors\' or \'update the website\' — you\'re hiding. Those aren\'t Ws. Those are comfort tasks disguised as work.',
      },
      {
        title: 'One sentence, one outcome',
        body: 'If your W takes more than one sentence to describe, it\'s too vague. A good W names the deliverable, the count, and what \'done\' looks like. Example: \'Send 3 personalized outreach messages to SaaS founders — all follow-ups scheduled.\'',
      },
      {
        title: 'Completable today',
        body: 'If the W can\'t be finished today, it\'s a project — not a W. Break it down until it fits in one day. If you keep saying \'Partial\' at end of day, your Ws are too big.',
      },
      {
        title: 'Block 1 = Eat the frog',
        body: 'Your sharpest 90 minutes go to the Primary W. Always. This is the revenue-moving action — outreach, sales calls, demos, follow-ups. Don\'t waste peak energy on admin or planning.',
      },
      {
        title: 'Structure your 4 blocks',
        body: 'Block 1: Primary W (revenue action). Block 2: Build (ship what a customer needs — only valid if someone is waiting for it). Block 3: Secondary W (partnerships, content, systems). Block 4: Learning/admin (Scale to $1M reading, news catch-up — low stakes, high recharge).',
      },
      {
        title: 'The NOT list before each block',
        body: 'Before pressing Start on the focus timer, decide what you will NOT do. No Slack until the block ends. No email. No \'quick check.\' Your identified blockers are aimless email, browsing, and extended team conversations. You already know the enemies. Name them before each block.',
      },
      {
        title: 'Move between blocks',
        body: 'Even 5 minutes of movement between blocks directly fuels the next one. Do a round of Surya Namakara between blocks instead of trying to find a separate slot. This feeds your T score and your energy simultaneously.',
      },
      {
        title: 'Close the loop (3 minutes)',
        body: 'End of day: Did the W ship? Answer honestly, binary. Score T — did you move, eat well, read, rest? Name the blocker if there was one. Plant tomorrow\'s seed. Close the day. Don\'t leave open loops — they drain cognitive energy and make tomorrow\'s morning harder.',
      },
      {
        title: 'Sunday review — 5 minutes',
        body: 'Look at History every Sunday. Two questions only: What\'s my W ship rate? What blocker keeps repeating? If you shipped 4 out of 5 days, great. If \'Slack\' appears as a blocker 3 times, you don\'t need a framework — you need to mute Slack during blocks.',
      },
      {
        title: 'The one thing that will make or break you',
        body: 'At pre-revenue, the only W that matters is getting in front of people who might pay you. Everything else feels productive but isn\'t. Your daily W should be a bite-sized piece of your monthly goal — \'Message 3 specific founders today with a personalized pitch.\' Not \'work on outreach strategy.\' Not \'update CRM.\' Three people. Specific. Measurable. Done by lunch. The system is built. Use it ruthlessly.',
      },
    ],
  },

  '/focus': {
    pageLabel: 'FOCUS BLOCKS TIPS',
    intro: null,
    tips: [
      {
        title: 'Write the NOT list first',
        body: 'Before pressing Start, decide what you will NOT do during this block. No Slack until it ends. No email. No \'quick check.\' The pre-commitment is everything — decide before temptation arrives.',
      },
      {
        title: 'First block = Primary W',
        body: 'Your sharpest 90 minutes go to the primary W. Always. Don\'t waste peak energy on admin, planning, or secondary tasks. Eat the frog when your teeth are sharpest.',
      },
      {
        title: 'Interruptions aren\'t minutes — they\'re blocks',
        body: 'A 2-minute Slack check doesn\'t cost 2 minutes. It costs 15–25 minutes of cognitive reload. Three interruptions don\'t cost you 6 minutes — they cost the entire 90-minute block.',
      },
      {
        title: 'Move between blocks',
        body: 'Even 5 minutes of movement between blocks directly fuels the next one. Do a round of Surya Namakara. Walk. Stretch. This is T-score fuel, not wasted time.',
      },
      {
        title: 'Block structure for founders',
        body: 'Block 1: Primary W (revenue action — outreach, calls, demos). Block 2: Build (ship what a customer needs). Block 3: Secondary W (partnerships, content, systems). Block 4: Learning and admin (low stakes, high recharge).',
      },
    ],
  },

  '/score': {
    pageLabel: 'END-OF-DAY SCORE TIPS',
    intro: null,
    tips: [
      {
        title: 'Binary, not feelings',
        body: 'Did the W ship — yes or no? Don\'t score how the day felt. Score whether the outcome happened. A day that felt chaotic but shipped the W is a win. A day that felt productive but didn\'t ship is not.',
      },
      {
        title: 'Partial means too big',
        body: 'If you\'re saying \'Partial\' more than once a week, your Ws are scoped too large. Break them down until \'Yes\' is the normal answer.',
      },
      {
        title: 'Name the blocker, don\'t journal',
        body: 'One line. Just name it. \'Slack rabbit hole.\' \'Energy crash after lunch.\' \'Unclear spec.\' You\'re building a dataset — patterns will surface over weeks without any analysis effort.',
      },
      {
        title: 'Seed tomorrow before you close',
        body: 'Plant tomorrow\'s W seed while today is still fresh. Don\'t leave it to morning-brain. Evening-you has context that morning-you won\'t remember.',
      },
      {
        title: 'A 4/10 honest day beats an 8/10 inflated day',
        body: 'The scoring system only works if the data is real. Inflating scores feels good tonight but breaks your feedback loop for the rest of the month.',
      },
    ],
  },

  '/history': {
    pageLabel: 'HISTORY TIPS',
    intro: null,
    tips: [
      {
        title: 'Sunday review — 5 minutes only',
        body: 'Every Sunday, look at the week. Two questions: What\'s my W ship rate? What blocker keeps repeating? That\'s it. No journaling, no planning, no strategy session. Just pattern recognition.',
      },
      {
        title: 'Three of the same blocker = structural problem',
        body: 'If the same blocker shows up three times in a week, willpower isn\'t the fix. Change the environment. Mute the app. Block the site. Move the phone.',
      },
      {
        title: 'Watch for comfort W streaks',
        body: 'If your Ws for the week are all comfortable tasks — building features, organizing, researching — and none involve talking to customers or making offers, the avoidance is systemic. Next Monday\'s W should scare you slightly.',
      },
    ],
  },

  '/canvas': {
    pageLabel: 'COMMAND CANVAS TIPS',
    intro: null,
    tips: [
      {
        title: 'Left to right = your day',
        body: 'Monthly Focus gives direction. Today\'s W gives clarity. Focus Timer gives execution. Close the Loop gives honesty. Work the canvas left to right.',
      },
      {
        title: 'The equation is live',
        body: 'R = (W × C) + T updates in real time. Watch it. If R is low, one variable is dragging. Usually it\'s C — most people set a decent W but fragment their focus.',
      },
      {
        title: 'Close the loop on the same screen',
        body: 'Don\'t leave the canvas without closing. Open loops drain cognitive energy and make tomorrow\'s morning harder. Close today before you close the laptop.',
      },
    ],
  },

  '/monthly': {
    pageLabel: 'MONTHLY RESULTS TIPS',
    intro: null,
    tips: [
      {
        title: 'Three domains maximum',
        body: 'Business, Health, Learning. That\'s enough. Adding more domains dilutes focus. Each domain should have 1–3 specific, measurable targets for the month.',
      },
      {
        title: 'Monthly goals feed daily Ws',
        body: 'Every morning, glance at monthly goals before setting the W. The W should be a bite-sized piece of a monthly goal — not a random task that felt urgent.',
      },
      {
        title: 'Rewrite goals that don\'t generate Ws',
        body: 'If a monthly goal hasn\'t produced a daily W in two weeks, it\'s either not important or not specific enough. Rewrite it or drop it.',
      },
    ],
  },
}

export function getTipsForPath(pathname) {
  return TIPS[pathname] || null
}
